/**
 * OfflineBanner — top-fixed status banner shown when the user is offline OR
 * has queued mutations awaiting sync.
 *
 * Dismissable; the dismissed state is held in component memory only, so the
 * next offline transition re-shows it.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { peekAll, subscribe, type OfflineEntry } from "../../sync/offlineQueue";
import { SYNC_COMPLETED_EVENT } from "../../sync/syncWorker";

export function OfflineBanner() {
  const { t } = useTranslation("sync");
  const { isOnline } = useOnlineStatus();
  const [dismissed, setDismissed] = useState(false);
  const wasOnlineRef = useRef(isOnline);
  const [queue, setQueue] = useState<OfflineEntry[]>([]);

  useEffect(() => {
    let active = true;
    peekAll().then((entries) => {
      if (active) setQueue(entries);
    });
    const unsub = subscribe((entries) => {
      if (active) setQueue(entries);
    });
    const onSync = () => {
      peekAll().then((entries) => {
        if (active) setQueue(entries);
      });
    };
    if (typeof window !== "undefined") {
      window.addEventListener(SYNC_COMPLETED_EVENT, onSync);
    }
    return () => {
      active = false;
      unsub();
      if (typeof window !== "undefined") {
        window.removeEventListener(SYNC_COMPLETED_EVENT, onSync);
      }
    };
  }, []);

  // Re-show on every offline transition.
  useEffect(() => {
    if (wasOnlineRef.current && !isOnline) {
      setDismissed(false);
    }
    wasOnlineRef.current = isOnline;
  }, [isOnline]);

  const queueDepth = queue.length;
  const visible = !dismissed && (!isOnline || queueDepth > 0);
  if (!visible) return null;

  const countLabel =
    queueDepth === 0
      ? null
      : queueDepth === 1
        ? t("offline_banner.queue_count_one", { count: queueDepth })
        : t("offline_banner.queue_count_other", { count: queueDepth });

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={t("offline_banner.title")}
      data-testid="offline-banner"
      className="fixed top-0 start-0 end-0 z-50 flex items-center justify-between gap-3 border-b border-amber-200 bg-amber-50 ps-4 pe-2 py-2 text-amber-900 shadow-sm dark:border-amber-900 dark:bg-amber-950 dark:text-amber-100"
    >
      <div className="flex min-w-0 flex-col">
        <span className="text-sm font-semibold">
          {isOnline ? t("offline_banner.syncing_title") : t("offline_banner.title")}
        </span>
        <span className="text-xs">
          {isOnline ? t("offline_banner.syncing_description") : t("offline_banner.description")}
          {countLabel ? ` — ${countLabel}` : null}
        </span>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t("offline_banner.dismiss")}
        className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-md hover:bg-amber-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:hover:bg-amber-900"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
}
